import { CadFile, DynamicAssetModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { DynamicAssetCommonPropertiesModel } from '../../../../../typings/api/skymap/rest/v1/.common';
import { TokenService } from '../../../services/authentication/token.service';
import { DynamicAssetService } from '../../../services/axios/services/v0/dynamic-asset-service';
import { SharedViewService } from '../../../services/axios/services/v0/shared-view-service';
import { WebApi } from '../../../services/web-api';
import { inflateArrayBuffer } from '../../../utils/compressionUtils';
import { isDefined } from '../../../utils/variables';
import { CoordinateManager } from '../../coordinate-manager';
import { Dxf, DxfOptions } from '../../elements/dxf';
import { ThreeDxfData } from '../../elements/dxf/types';
import { FileInfo } from '../../elements/file-info/types';
import { getWebWorkerManager } from '../../workers/web-worker-manager';
// @ts-ignore
import { parseUtilities } from './dxfParseUtilities';

export class DxfFactory {
  static _instance: DxfFactory = new DxfFactory();
  static instance(): DxfFactory {
    return DxfFactory._instance;
  }
  private tokenService?: TokenService;
  private sharedViewService?: SharedViewService;
  private dynamicAssetService?: DynamicAssetService;

  /**
   *  Set the ProjectService to be able to load DXF files from the project documents
   */
  initialise(dynamicAssetService: DynamicAssetService, sharedViewService: SharedViewService) {
    this.dynamicAssetService = dynamicAssetService;
    this.sharedViewService = sharedViewService;
  }

  /**
   *  A function to load a DXF file based on a previously saved asset and its properties
   */
  loadDXFAsset(asset: DynamicAssetModel, cadFile: CadFile, sharedViewId?: string) {
    if (!asset.properties || !asset.properties.name || !asset.fileId) {
      console.error('CAD asset missing properties information');
    }

    const fileInfo: FileInfo = {
      ...cadFile.file,
      path: cadFile.path,
    };

    const properties = asset.properties as DynamicAssetCommonPropertiesModel;

    const options: DxfOptions = {
      ...properties,
      fileInfo,
      fileId: asset.fileId,
      timelineContainerId: asset.timelineContainerId,
      skyMapVolumeSourceIds: asset.skyMapVolumeSourceIds,
      prismVolumes: asset.targetPrismVolumes,
      assetTagIds: asset.assetTags?.map((tag) => tag.id) ?? [],
      // Always start hidden so that setStateVisibility(true) transitions false → true,
      // triggering parse(). Without this, a DXF with properties.visible=true would call
      // setStateVisibility(true) in the constructor against the default stateVisibility=true,
      // making it a no-op and preventing the file from ever being fetched.
      // The asset-manager re-applies visibility (including shared-view overrides) after creation.
      visible: false,
    };

    const newDXF = new Dxf(undefined, options, asset.id, sharedViewId);

    newDXF.setParseCallback(async (versionId: string | undefined, fileId: string | undefined) => {
      // todo: Handle parse errors.
      const result = await this.parseDXFFile(properties.name, versionId, sharedViewId, fileId);
      newDXF.setDXFData(result.dxf);
    });
    return newDXF;
  }

  async getDxfDataByUrl(url: string): Promise<string> {
    // Simple way to check if the data was processed via FME.
    const isParsedByFme = !url.includes('skymap.sim/');

    if (isParsedByFme) {
      const dxfData = await WebApi.fetchS3DataAsArrayBuffer(url);
      return inflateArrayBuffer(dxfData);
    }

    return await WebApi.fetchS3DataAsText(url);
  }

  async getDxfData(fileId?: string, versionId?: string, sharedViewId?: string) {
    if (!this.tokenService) {
      this.loadToken();
    }

    const url = await this.getDxfUrl(fileId, versionId, sharedViewId);
    return this.getDxfDataByUrl(url);
  }

  async getDxfUrl(fileId?: string, versionId?: string, sharedViewId?: string) {
    if (!fileId) {
      throw Error('No file ID was provided. Unable to get dxf data.');
    }

    const result = sharedViewId
      ? await this.sharedViewService!.getDxfSignedUrlV1({
          path: { id: sharedViewId, fileId: fileId },
          query: { fileVersionId: versionId },
        })
      : await this.dynamicAssetService!.getDxfSignedUrlV1({
          query: { fileId, fileVersionId: versionId },
        });

    // Get the data converted via ODA first hand, then fallback to FME.
    const url = result.convertedUrl ?? result.url;

    if (!isDefined(url)) {
      throw Error('Failed to get DXF URL. No url found.');
    }

    return url;
  }

  /**
   *  Load and parse a DXF file from th eproject documents
   */
  async parseDXFFile(
    name: string,
    versionId: string | undefined,
    sharedViewId?: string,
    fileId?: string,
  ): Promise<{
    dxf?: ThreeDxfData;
    bytes: number;
    error?: any;
  }> {
    if (!this.tokenService) {
      this.loadToken();
    }
    const url = await this.getDxfUrl(fileId, versionId, sharedViewId);

    const dxfData = await getWebWorkerManager().spawnDxfLoader(
      url,
      CoordinateManager.getInstance().getProjectionOrigo(),
    );

    const threeData = parseUtilities.getThreeData(dxfData.data);
    (threeData as any).fileName = name;
    return { dxf: threeData, bytes: dxfData.bytes };
  }

  loadToken() {
    this.tokenService = new TokenService();
  }
}
